
export default {
    name: 'ImageAnnotator',
    props: {
        src: {
            type: String,
            required: true,
        },
        adminName: {
            type: String,
            default: '',
        },
        busy: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            displaySrc: '',
            objectUrl: '',
            tool: '',
            strokes: [],
            currentStroke: null,
            tags: [],
            tagSeq: 0,
            frameW: 0,
            frameH: 0,
            preparing: true,
            drawing: false,
            confirming: false,
        };
    },
    computed: {
        absSrc() {
            let s = this.src || '';
            if (s.indexOf('//') === 0) {
                s = window.location.protocol + s;
            }
            return s;
        },
        frameStyle() {
            if (!this.frameW || !this.frameH) {
                return { visibility: 'hidden' };
            }
            return {
                width: this.frameW + 'px',
                height: this.frameH + 'px',
            };
        },
    },
    mounted() {
        let t = this;
        document.body.appendChild(t.$el);
        t._onResize = t.onResize.bind(t);
        window.addEventListener('resize', t._onResize);
        t.prepareSrc();
    },
        beforeDestroy() {
            let t = this;
            window.removeEventListener('resize', t._onResize);
            if (t.objectUrl) {
                URL.revokeObjectURL(t.objectUrl);
            }
        },
        methods: {
            async prepareSrc() {
                let t = this;
                t.preparing = true;
                try {
                    let res = await fetch(t.absSrc, { mode: 'cors', credentials: 'omit' });
                    if (!res.ok) {
                        throw new Error('fetch');
                    }
                    let blob = await res.blob();
                    t.objectUrl = URL.createObjectURL(blob);
                    t.displaySrc = t.objectUrl;
                } catch (e) {
                    t.displaySrc = t.absSrc;
                }
                t.$nextTick(function () {
                    let img = t.$refs.img;
                    if (img && img.complete && img.naturalWidth) {
                        t.onImgLoad();
                    }
                });
            },

            onImgLoad() {
                let t = this;
                t.preparing = false;
                t.layout();
            },

            onImgError() {
                this.preparing = false;
            },

            onAnnotatorClick(e) {
                if (e.target.closest && (e.target.closest('.annotator-tag') || e.target.closest('.annotator-frame'))) {
                    return;
                }
                this.commitEditingTags();
            },

        onResize() {
            this.layout();
        },

        layout() {
            let t = this;
            let stage = t.$refs.stage;
            let img = t.$refs.img;
            if (!stage || !img || !img.naturalWidth) {
                return;
            }
            let maxW = stage.clientWidth;
            let maxH = stage.clientHeight;
            let ratio = img.naturalWidth / img.naturalHeight;
            let w = maxW;
            let h = w / ratio;
            if (h > maxH) {
                h = maxH;
                w = h * ratio;
            }
            t.frameW = Math.max(1, Math.round(w));
            t.frameH = Math.max(1, Math.round(h));
            t.$nextTick(function () {
                t.resizeCanvas();
            });
        },

        resizeCanvas() {
            let t = this;
            let canvas = t.$refs.canvas;
            if (!canvas || !t.frameW) {
                return;
            }
            let dpr = window.devicePixelRatio || 1;
            canvas.width = Math.round(t.frameW * dpr);
            canvas.height = Math.round(t.frameH * dpr);
            canvas.style.width = t.frameW + 'px';
            canvas.style.height = t.frameH + 'px';
            t.redraw();
        },

        setTool(name) {
            let t = this;
            t.commitEditingTags();
            t.tool = t.tool === name ? '' : name;
        },

        relPoint(e) {
            let t = this;
            let rect = t.$refs.frame.getBoundingClientRect();
            let x = (e.clientX - rect.left) / rect.width;
            let y = (e.clientY - rect.top) / rect.height;
            return {
                x: Math.min(1, Math.max(0, x)),
                y: Math.min(1, Math.max(0, y)),
            };
        },

        onPointerDown(e) {
            let t = this;
            if (t.tool !== 'draw' || t.busy) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            t.commitEditingTags();
            t.drawing = true;
            t.currentStroke = [t.relPoint(e)];
            if (e.currentTarget.setPointerCapture) {
                e.currentTarget.setPointerCapture(e.pointerId);
            }
            t.redraw();
        },

        onPointerMove(e) {
            let t = this;
            if (!t.drawing || !t.currentStroke) {
                return;
            }
            e.preventDefault();
            t.currentStroke.push(t.relPoint(e));
            t.redraw();
        },

        onPointerUp(e) {
            let t = this;
            if (!t.drawing) {
                return;
            }
            if (e && e.preventDefault) {
                e.preventDefault();
            }
            t.drawing = false;
            if (t.currentStroke && t.currentStroke.length) {
                t.strokes.push(t.currentStroke);
            }
            t.currentStroke = null;
            t.redraw();
        },

        undo() {
            let t = this;
            if (!t.strokes.length) {
                return;
            }
            t.strokes.pop();
            t.redraw();
        },

        onFrameClick(e) {
            let t = this;
            if (t.tool !== 'text' || t.busy || t.drawing) {
                return;
            }
            if (e.target.closest && e.target.closest('.annotator-tag')) {
                return;
            }
            t.commitEditingTags();
            let pt = t.relPoint(e);
            t.addTag(pt.x, pt.y);
        },

        addTag(x, y) {
            let t = this;
            let tag = {
                id: ++t.tagSeq,
                x: x,
                y: y,
                text: '',
                editing: true,
            };
            t.tags.push(tag);
            t.$nextTick(function () {
                let el = t.$el.querySelector('.tag-input-' + tag.id);
                if (el) {
                    el.focus();
                }
            });
        },

        onTagClick(tag) {
            let t = this;
            if (t.tool === 'draw' || t.busy) {
                return;
            }
            if (tag.editing) {
                return;
            }
            t.commitEditingTags();
            tag.editing = true;
            t.$nextTick(function () {
                let el = t.$el.querySelector('.tag-input-' + tag.id);
                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },

        commitTag(tag) {
            if (!tag || !tag.editing) {
                return;
            }
            tag.text = (tag.text || '').trim();
            tag.editing = false;
            if (!tag.text) {
                this.removeTag(tag);
            }
        },

        commitEditingTags() {
            let t = this;
            t.tags.slice().forEach(function (tag) {
                if (tag.editing) {
                    t.commitTag(tag);
                }
            });
        },

        removeTag(tag) {
            let t = this;
            let idx = t.tags.indexOf(tag);
            if (idx > -1) {
                t.tags.splice(idx, 1);
            }
        },

        redraw() {
            let t = this;
            let canvas = t.$refs.canvas;
            if (!canvas) {
                return;
            }
            let dpr = window.devicePixelRatio || 1;
            let ctx = canvas.getContext('2d');
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            t.paintStrokes(ctx, t.frameW, t.frameH);
        },

        paintStrokes(ctx, w, h) {
            let t = this;
            let strokes = t.strokes.slice();
            if (t.currentStroke && t.currentStroke.length) {
                strokes.push(t.currentStroke);
            }
            let lineWidth = Math.max(3, Math.min(w, h) * 0.012);
            ctx.strokeStyle = '#FF3B30';
            ctx.fillStyle = '#FF3B30';
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = lineWidth;
            strokes.forEach(function (points) {
                if (!points || !points.length) {
                    return;
                }
                if (points.length === 1) {
                    ctx.beginPath();
                    ctx.arc(points[0].x * w, points[0].y * h, lineWidth / 2, 0, Math.PI * 2);
                    ctx.fill();
                    return;
                }
                ctx.beginPath();
                ctx.moveTo(points[0].x * w, points[0].y * h);
                for (let i = 1; i < points.length - 1; i++) {
                    let mx = (points[i].x + points[i + 1].x) / 2 * w;
                    let my = (points[i].y + points[i + 1].y) / 2 * h;
                    ctx.quadraticCurveTo(points[i].x * w, points[i].y * h, mx, my);
                }
                let last = points[points.length - 1];
                ctx.lineTo(last.x * w, last.y * h);
                ctx.stroke();
            });
        },

        paintTags(ctx, w, h) {
            let t = this;
            let fontSize = Math.max(16, Math.round(Math.min(w, h) * 0.028));
            ctx.font = '600 ' + fontSize + 'px sans-serif';
            ctx.textBaseline = 'middle';
            t.tags.forEach(function (tag) {
                let text = (tag.text || '').trim();
                if (!text) {
                    return;
                }
                let label = (t.adminName || '') + '：' + text;
                let x = tag.x * w;
                let y = tag.y * h;
                let padX = fontSize * 0.55;
                let padY = fontSize * 0.38;
                let textW = ctx.measureText(label).width;
                let bw = textW + padX * 2;
                let bh = fontSize + padY * 2;
                let gap = fontSize * 0.7;
                let flip = tag.x > 0.62;
                let bx = flip ? x - gap - bw : x + gap;
                let by = y - bh / 2;
                if (bx < 8) {
                    bx = 8;
                }
                if (bx + bw > w - 8) {
                    bx = w - 8 - bw;
                }
                if (by < 8) {
                    by = 8;
                }
                if (by + bh > h - 8) {
                    by = h - 8 - bh;
                }

                ctx.beginPath();
                ctx.arc(x, y, fontSize * 0.28, 0, Math.PI * 2);
                ctx.fillStyle = '#FF3B30';
                ctx.fill();
                ctx.lineWidth = Math.max(1.5, fontSize * 0.08);
                ctx.strokeStyle = '#fff';
                ctx.stroke();

                t.roundRect(ctx, bx, by, bw, bh, fontSize * 0.28);
                ctx.fillStyle = 'rgba(0,0,0,0.72)';
                ctx.fill();

                ctx.fillStyle = '#fff';
                ctx.fillText(label, bx + padX, by + bh / 2);
            });
        },

        roundRect(ctx, x, y, w, h, r) {
            if (w < 2 * r) {
                r = w / 2;
            }
            if (h < 2 * r) {
                r = h / 2;
            }
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.arcTo(x + w, y, x + w, y + h, r);
            ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r);
            ctx.arcTo(x, y, x + w, y, r);
            ctx.closePath();
        },

        bake() {
            let t = this;
            return new Promise(function (resolve, reject) {
                let img = t.$refs.img;
                if (!img || !img.naturalWidth) {
                    reject(new Error('image'));
                    return;
                }
                let W = img.naturalWidth;
                let H = img.naturalHeight;
                let canvas = document.createElement('canvas');
                canvas.width = W;
                canvas.height = H;
                let ctx = canvas.getContext('2d');
                try {
                    ctx.drawImage(img, 0, 0, W, H);
                } catch (e) {
                    reject(e);
                    return;
                }
                t.paintStrokes(ctx, W, H);
                t.paintTags(ctx, W, H);
                try {
                    canvas.toBlob(function (blob) {
                        if (!blob) {
                            reject(new Error('toBlob'));
                            return;
                        }
                        resolve(blob);
                    }, 'image/jpeg', 0.85);
                } catch (e) {
                    reject(e);
                }
            });
        },

        onCancel() {
            if (this.busy) {
                return;
            }
            this.$emit('cancel');
        },

        async onConfirm() {
            let t = this;
            if (t.busy || t.preparing || t.confirming) {
                return;
            }
            t.commitEditingTags();
            let hasStroke = t.strokes.length > 0;
            let hasTag = t.tags.some(function (tag) {
                return (tag.text || '').trim();
            });
            if (!hasStroke && !hasTag) {
                t.$emit('cancel');
                return;
            }
            t.confirming = true;
            try {
                let blob = await t.bake();
                t.$emit('confirm', blob);
                t.confirming = false;
            } catch (e) {
                t.confirming = false;
                A.toast('保存に失敗しました');
            }
        },
    },
};
